import {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type Ref,
  type RefObject,
} from 'react';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  Maximize2,
  MoveHorizontal,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

import { fetchRedactionPageImage } from '../../lib/redactionApi';
import type {
  RedactionBox,
  RedactionOverlayBox,
  RedactionPageGeometry,
} from '../../types/redaction';

export interface RedactionDocumentViewHandle {
  /** Scroll so the box sits comfortably in view. */
  scrollToBox: (box: RedactionBox) => void;
}

interface RedactionDocumentViewProps {
  recordId: string;
  pages: RedactionPageGeometry[];
  pagesWithImages: number[];
  pagesOcrd?: number[];
  /** Candidate boxes for every page; each page shows its own. */
  boxes: RedactionOverlayBox[];
  /** Boxes the reviewer drew by hand. */
  drawnBoxes: RedactionOverlayBox[];
  onDrawBox: (box: RedactionBox) => void;
  /** Called with the owning item's id when a rectangle is clicked. */
  onSelectBox?: (ownerId: string) => void;
  ref?: Ref<RedactionDocumentViewHandle>;
}

const MIN_BOX_POINTS = 4;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];
/** Padding around the pages, in pixels. */
const GUTTER = 16;
/** Room taken by a page's caption, used when fitting a whole page on screen. */
const CAPTION_HEIGHT = 28;
const NO_BOXES: RedactionOverlayBox[] = [];

const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

/**
 * The document as one continuous column of pages, with the proposed boxes
 * drawn over them.
 *
 * Pages take their size from the geometry the API reported, so the column has
 * its full height before any image arrives and scrolling does not jump as
 * pages load. Each page fetches its image when it nears the viewport and drops
 * it again when it is well clear, which keeps a long document from holding
 * every page in memory.
 *
 * Boxes are positioned as percentages of the page's own point dimensions, so
 * the overlay stays aligned at any zoom.
 */
export default function RedactionDocumentView({
  recordId,
  pages,
  pagesWithImages,
  pagesOcrd = [],
  boxes,
  drawnBoxes,
  onDrawBox,
  onSelectBox,
  ref,
}: RedactionDocumentViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pageEls = useRef(new Map<number, HTMLDivElement>());
  const frame = useRef<number | null>(null);
  const lastZoom = useRef(1);
  const pendingPage = useRef<number | null>(null);
  const drawHandler = useRef(onDrawBox);
  const selectHandler = useRef(onSelectBox);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  // Null means show the current page. A string is the value being typed, so
  // "12" does not jump to page 1 on the way through.
  const [pageDraft, setPageDraft] = useState<string | null>(null);

  const lastPage = pages.length;
  const displayedPage = pageDraft ?? String(currentPage + 1);
  const pageWidth = Math.max(80, (size.width - GUTTER * 2) * zoom);

  useEffect(() => {
    drawHandler.current = onDrawBox;
  }, [onDrawBox]);

  useEffect(() => {
    selectHandler.current = onSelectBox;
  }, [onSelectBox]);

  const stableDrawBox = useCallback((box: RedactionBox) => {
    drawHandler.current(box);
  }, []);

  const stableSelectBox = useCallback((ownerId: string) => {
    selectHandler.current?.(ownerId);
  }, []);

  const registerPage = useCallback((page: number, el: HTMLDivElement | null) => {
    if (el) pageEls.current.set(page, el);
    else pageEls.current.delete(page);
  }, []);

  const boxesByPage = useMemo(() => groupByPage(boxes), [boxes]);
  const drawnByPage = useMemo(() => groupByPage(drawnBoxes), [drawnBoxes]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setSize({ width: rect.width, height: rect.height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const updateCurrentPage = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const line = container.getBoundingClientRect().top + container.clientHeight / 3;
    let current = pages[0]?.number ?? 0;
    for (const geometry of pages) {
      const el = pageEls.current.get(geometry.number);
      if (!el) continue;
      if (el.getBoundingClientRect().top <= line) current = geometry.number;
      else break;
    }
    setCurrentPage(current);
  }, [pages]);

  const handleScroll = () => {
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      updateCurrentPage();
    });
  };

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  const scrollToPage = useCallback((page: number) => {
    pageEls.current.get(page)?.scrollIntoView({ block: 'start' });
  }, []);

  // Zooming resizes every page, so keep the reader at the same place in the
  // document by scaling the scroll offset with it.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container && lastZoom.current !== zoom) {
      const ratio = zoom / lastZoom.current;
      container.scrollTop *= ratio;
      container.scrollLeft *= ratio;
      lastZoom.current = zoom;
    }
    if (pendingPage.current !== null) {
      scrollToPage(pendingPage.current);
      pendingPage.current = null;
    }
  }, [zoom, scrollToPage]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      setZoom((current) => clampZoom(current * Math.exp(-event.deltaY * 0.01)));
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      scrollToBox(box) {
        const container = containerRef.current;
        const el = pageEls.current.get(box.page);
        const geometry = pages.find((candidate) => candidate.number === box.page);
        const surface = el?.querySelector<HTMLElement>('[data-surface]');
        if (!container || !surface || !geometry) return;

        const containerRect = container.getBoundingClientRect();
        const surfaceRect = surface.getBoundingClientRect();
        const top =
          surfaceRect.top -
          containerRect.top +
          container.scrollTop +
          (box.y0 / geometry.height) * surfaceRect.height -
          container.clientHeight / 3;
        const left =
          surfaceRect.left -
          containerRect.left +
          container.scrollLeft +
          (box.x0 / geometry.width) * surfaceRect.width -
          container.clientWidth / 3;
        container.scrollTo({ top: Math.max(0, top), left: Math.max(0, left) });
      },
    }),
    [pages],
  );

  const zoomIn = () => {
    setZoom((current) => ZOOM_STEPS.find((step) => step > current + 0.001) ?? MAX_ZOOM);
  };

  const zoomOut = () => {
    setZoom(
      (current) =>
        [...ZOOM_STEPS].reverse().find((step) => step < current - 0.001) ?? MIN_ZOOM,
    );
  };

  const fitPage = () => {
    const geometry = pages.find((candidate) => candidate.number === currentPage);
    if (!geometry || size.width <= 0) return;
    const availableWidth = size.width - GUTTER * 2;
    const availableHeight = size.height - GUTTER * 2 - CAPTION_HEIGHT;
    const heightAtFitWidth = availableWidth * (geometry.height / geometry.width);
    const next = clampZoom(availableHeight / heightAtFitWidth);
    if (Math.abs(next - zoom) < 0.001) {
      scrollToPage(currentPage);
    } else {
      pendingPage.current = currentPage;
      setZoom(next);
    }
  };

  const goToPage = (page: number) => {
    const bounded = Math.min(lastPage - 1, Math.max(0, page));
    setCurrentPage(bounded);
    scrollToPage(bounded);
  };

  const goToEnteredPage = () => {
    const parsed = Number.parseInt(displayedPage, 10);
    setPageDraft(null);
    if (!Number.isFinite(parsed) || lastPage < 1) return;
    goToPage(parsed - 1);
  };

  const toolbarButton =
    'rounded-lg border border-gray-200 bg-white p-1.5 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-gray-200 bg-white px-4 py-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 0}
            className={toolbarButton}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <form
            className="flex items-center gap-1.5"
            onSubmit={(event) => {
              event.preventDefault();
              goToEnteredPage();
            }}
          >
            <label htmlFor="redaction-go-to-page" className="text-sm text-gray-500">
              Page
            </label>
            <input
              id="redaction-go-to-page"
              type="number"
              min={1}
              max={lastPage}
              inputMode="numeric"
              value={displayedPage}
              disabled={lastPage <= 1}
              onChange={(event) => setPageDraft(event.target.value)}
              onBlur={() => setPageDraft(null)}
              className="w-14 rounded-lg border border-gray-200 bg-white px-1.5 py-1 text-center text-sm font-semibold tabular-nums text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-40 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <span className="text-sm tabular-nums text-gray-500">of {lastPage}</span>
          </form>
          <button
            type="button"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= lastPage - 1}
            className={toolbarButton}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM + 0.001}
            className={toolbarButton}
            aria-label="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="w-12 text-center text-sm font-semibold tabular-nums text-gray-700">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM - 0.001}
            className={toolbarButton}
            aria-label="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            className={toolbarButton}
            aria-label="Fit page width"
            title="Fit width"
          >
            <MoveHorizontal className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={fitPage}
            className={toolbarButton}
            aria-label="Fit whole page"
            title="Fit page"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>

        <div className="hidden items-center gap-1.5 text-xs text-gray-500 xl:flex">
          <Eye className="h-3.5 w-3.5" />
          Click a box to find it in the list. Hover to see under it. Drag to draw one.
        </div>
      </div>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="min-h-0 flex-1 overflow-auto bg-gray-100"
      >
        {size.width > 0 && (
          <div
            className="flex w-max min-w-full flex-col items-center gap-6"
            style={{ padding: GUTTER }}
          >
            {pages.length === 0 && (
              <p className="py-12 text-sm text-gray-500">This document has no pages.</p>
            )}
            {pages.map((geometry) => (
              <PageSurface
                key={geometry.number}
                recordId={recordId}
                geometry={geometry}
                width={pageWidth}
                root={containerRef}
                hasImageContent={pagesWithImages.includes(geometry.number)}
                wasOcrd={pagesOcrd.includes(geometry.number)}
                boxes={boxesByPage.get(geometry.number) ?? NO_BOXES}
                drawnBoxes={drawnByPage.get(geometry.number) ?? NO_BOXES}
                registerPage={registerPage}
                onDrawBox={stableDrawBox}
                onSelectBox={stableSelectBox}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface PageSurfaceProps {
  recordId: string;
  geometry: RedactionPageGeometry;
  width: number;
  root: RefObject<HTMLDivElement | null>;
  hasImageContent: boolean;
  wasOcrd: boolean;
  boxes: RedactionOverlayBox[];
  drawnBoxes: RedactionOverlayBox[];
  registerPage: (page: number, el: HTMLDivElement | null) => void;
  onDrawBox: (box: RedactionBox) => void;
  onSelectBox: (ownerId: string) => void;
}

const PageSurface = memo(function PageSurface({
  recordId,
  geometry,
  width,
  root,
  hasImageContent,
  wasOcrd,
  boxes,
  drawnBoxes,
  registerPage,
  onDrawBox,
  onSelectBox,
}: PageSurfaceProps) {
  const page = geometry.number;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);

  const [near, setNear] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [draftBox, setDraftBox] = useState<RedactionBox | null>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    registerPage(page, el);
    return () => registerPage(page, null);
  }, [page, registerPage]);

  // Fetch when the page is within a screen or so of the viewport, and let go
  // of the image again once it is well clear of it.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const latest = entries[entries.length - 1];
        if (latest) setNear(latest.isIntersecting);
      },
      { root: root.current, rootMargin: '150% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [root]);

  useEffect(() => {
    if (!near) return;
    let cancelled = false;
    let objectUrl: string | null = null;

    fetchRedactionPageImage(recordId, page)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setImageUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setImageUrl((current) => (current === objectUrl ? null : current));
      setFailed(false);
    };
  }, [recordId, page, near, attempt]);

  const isLoading = near && !imageUrl && !failed;

  const pointOnPage = (clientX: number, clientY: number) => {
    const overlay = overlayRef.current;
    if (!overlay) return null;
    const bounds = overlay.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return null;
    const x = ((clientX - bounds.left) / bounds.width) * geometry.width;
    const y = ((clientY - bounds.top) / bounds.height) * geometry.height;
    return {
      x: Math.min(geometry.width, Math.max(0, x)),
      y: Math.min(geometry.height, Math.max(0, y)),
    };
  };

  const boxFromCorners = (
    a: { x: number; y: number },
    b: { x: number; y: number },
  ): RedactionBox => ({
    page,
    x0: Math.min(a.x, b.x),
    y0: Math.min(a.y, b.y),
    x1: Math.max(a.x, b.x),
    y1: Math.max(a.y, b.y),
  });

  const startDraw = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button')) return;
    const origin = pointOnPage(event.clientX, event.clientY);
    if (!origin) return;
    event.preventDefault();
    dragOrigin.current = origin;
    setDraftBox(boxFromCorners(origin, origin));
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDraw = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragOrigin.current) return;
    const current = pointOnPage(event.clientX, event.clientY);
    if (!current) return;
    setDraftBox(boxFromCorners(dragOrigin.current, current));
  };

  const endDraw = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragOrigin.current) return;
    const current = pointOnPage(event.clientX, event.clientY);
    const origin = dragOrigin.current;
    dragOrigin.current = null;
    setDraftBox(null);
    if (overlayRef.current?.hasPointerCapture(event.pointerId)) {
      overlayRef.current.releasePointerCapture(event.pointerId);
    }
    if (!current) return;
    const box = boxFromCorners(origin, current);
    if (box.x1 - box.x0 < MIN_BOX_POINTS || box.y1 - box.y0 < MIN_BOX_POINTS) {
      return;
    }
    onDrawBox(box);
  };

  return (
    <div
      ref={wrapperRef}
      data-page={page}
      className="shrink-0 scroll-mt-4"
      style={{ width }}
    >
      <div className="mb-1.5 flex items-start gap-3 text-xs text-gray-500">
        <span className="shrink-0 font-semibold tabular-nums text-gray-600">
          Page {page + 1}
        </span>
        {hasImageContent && (
          <span className="flex items-start gap-1 text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {wasOcrd
              ? 'Contains an image. Text in it was read automatically, but a face or signature cannot be detected, so draw a box over anything the search missed.'
              : 'Contains an image. Text inside an image is not found by searching, and a face or signature cannot be detected at all, so draw a box over anything that should be withheld.'}
          </span>
        )}
      </div>

      <div
        ref={overlayRef}
        data-surface
        className="relative touch-none select-none bg-white shadow-sm"
        style={{ aspectRatio: `${geometry.width} / ${geometry.height}` }}
        {...(imageUrl
          ? {
              onPointerDown: startDraw,
              onPointerMove: moveDraw,
              onPointerUp: endDraw,
              onPointerCancel: endDraw,
            }
          : {})}
      >
        {imageUrl && (
          <>
            <img
              src={imageUrl}
              alt={`Page ${page + 1}`}
              draggable={false}
              className="pointer-events-none block h-full w-full"
            />
            {boxes.map(({ box, ownerId }, index) => (
              <OverlayBox
                key={`candidate-${box.page}-${box.x0}-${box.y0}-${index}`}
                box={box}
                geometry={geometry}
                className="bg-gray-900 opacity-100 hover:opacity-10 focus:opacity-10"
                label="Proposed redaction. Click to find it in the list, hover or focus to reveal the content underneath."
                onClick={() => onSelectBox(ownerId)}
              />
            ))}
            {drawnBoxes.map(({ box, ownerId }, index) => (
              <OverlayBox
                key={`drawn-${box.page}-${box.x0}-${box.y0}-${index}`}
                box={box}
                geometry={geometry}
                className="bg-emerald-900/90 ring-2 ring-emerald-400 opacity-100 hover:opacity-10 focus:opacity-10"
                label="Drawn redaction. Click to find it in the list, hover or focus to reveal the content underneath."
                onClick={() => onSelectBox(ownerId)}
              />
            ))}
            {draftBox && (
              <div
                className="pointer-events-none absolute border-2 border-dashed border-emerald-500 bg-emerald-500/20"
                style={boxStyle(draftBox, geometry)}
              />
            )}
          </>
        )}

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        )}

        {failed && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-gray-500">
            This page could not be rendered.
            <button
              type="button"
              onClick={() => setAttempt((count) => count + 1)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

function OverlayBox({
  box,
  geometry,
  className,
  label,
  onClick,
}: {
  box: RedactionBox;
  geometry: RedactionPageGeometry;
  className: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      style={boxStyle(box, geometry)}
      className={`absolute cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 ${className}`}
      aria-label={label}
      onClick={onClick}
    />
  );
}

function groupByPage(
  boxes: RedactionOverlayBox[],
): Map<number, RedactionOverlayBox[]> {
  const grouped = new Map<number, RedactionOverlayBox[]>();
  for (const entry of boxes) {
    const existing = grouped.get(entry.box.page);
    if (existing) existing.push(entry);
    else grouped.set(entry.box.page, [entry]);
  }
  return grouped;
}

function boxStyle(box: RedactionBox, geometry: RedactionPageGeometry) {
  return {
    left: `${(box.x0 / geometry.width) * 100}%`,
    top: `${(box.y0 / geometry.height) * 100}%`,
    width: `${((box.x1 - box.x0) / geometry.width) * 100}%`,
    height: `${((box.y1 - box.y0) / geometry.height) * 100}%`,
  };
}
